
import Vue from "vue"
import * as am4core from "@amcharts/amcharts4/core"
import {Label} from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import Component from "vue-class-component"
import {theme} from "@/theme"
import {Prop} from "vue-property-decorator"
import {MetricSliced, MetricStore} from "@/cube/metric-data/MetricStore"
import {MemberSnapshotMeasure, SuperScoreCube, SuperScoreDimension} from "@/cube"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {toKey} from "@/cube/toKey"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import _ from "lodash"
import {toPercent} from "@/cube/metric-data/toPercent"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import {createChangeRateLabel} from "@/ui/chart/updateChangeRate"
import {dropSortPrefixAdapter} from "@/ui/chart/dropSortPrefixAdapter"
import NoDataMessage from "@/ui/NoDataMessage.vue"
import {dimensionSettings} from "@/cube/settings"

@Component({
  components: {NoDataMessage},
})
export default class LifeInsuranceChart extends Vue {
  @Prop()
  metricStore: MetricStore

  chart: am4charts.PieChart | null

  labels: {titleLabel: Label; valueLabel: Label}

  private hasData = true
  private ready = false

  mounted() {
    const metricData = this.metricStore.getSliced(MemberSnapshotMeasure.countMembers, [
      SuperScoreDimension.lifeInsuranceIndicatorResult,
    ])

    this.$watch(
      () => metricData.end,
      () => {
        if (metricData.end) {
          if (this.chart) {
            this.hasData = metricData.end.length > 0
            if (this.hasData) {
              this.chart.show()
              this.updateChartData(metricData, this.labels)
            } else {
              this.chart.hide()
            }
          } else {
            this.chart = this.createChart(metricData)
            this.ready = true
            this.labels = this.createMiddleLabels(this.chart, metricData)
          }
        }
      },
      {immediate: true}
    )

    this.$watch(
      () => metricData.dateRange,
      () => {
        if (this.chart?.legend) {
          this.chart.legend.invalidateData()
        }
      }
    )
  }

  beforeDestroy() {
    this.chart?.dispose()
  }

  private hasCalculatorResults(data: any) {
    return !(data.length === 1 && data[0][SuperScoreCube.lifeInsuranceIndicatorResult] === "4|None")
  }

  private updateChartData(
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>,
    middleLabels: {titleLabel: am4core.Label; valueLabel: am4core.Label}
  ) {
    keyedDataUpdate(
      metricData.metric,
      (row: any) => toKey(row, [SuperScoreCube.lifeInsuranceIndicatorResult]),
      metricData.end!,
      this.chart!.data
    )
    this.chart!.invalidateRawData()

    this.updateMiddleLabelText(metricData, middleLabels)
  }

  private updateMiddleLabelText(
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>,
    middleLabels: {titleLabel: Label; valueLabel: Label}
  ) {
    const onTargetPct = _.round(
      toPercent(metricData, {
        [SuperScoreCube.lifeInsuranceIndicatorResult]: "On target",
      }) * 100
    )

    middleLabels.valueLabel.text = `${onTargetPct}%`
  }

  private getSeriesColour(insuranceIndicatorResult: string) {
    const {color} = dimensionSettings(
      SuperScoreCube.lifeInsuranceIndicatorResult,
      insuranceIndicatorResult
    )
    if (color === undefined) throw Error(`No color defined for ${insuranceIndicatorResult}`)
    else return color
  }

  private createChart(metricData: MetricSliced<MemberSnapshotMeasure.countMembers>) {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.PieChart)

    if (this.hasCalculatorResults(metricData.end)) {
      chart.data = <any>(
        metricData.end?.filter(
          dataItem => dataItem[SuperScoreCube.lifeInsuranceIndicatorResult] !== "4|None"
        )
      )
    } else {
      chart.data = <any>metricData.end
    }

    chart.data.forEach(
      dataItem =>
        (dataItem.color = this.getSeriesColour(
          removeDimensionSortPrefix(dataItem[SuperScoreCube.lifeInsuranceIndicatorResult])
        ))
    )
    chart.radius = am4core.percent(65)
    chart.innerRadius = am4core.percent(chart.radius.percent - 12)

    chart.startAngle = 180
    chart.endAngle = 360

    const series = chart.series.push(new am4charts.PieSeries())
    series.dataFields.value = MemberSnapshotMeasure.countMembers
    series.dataFields.category = SuperScoreCube.lifeInsuranceIndicatorResult
    series.filters.push(dropShadowFilter())
    series.slices.template.strokeWidth = 2
    series.slices.template.stroke = am4core.color("white")

    series.slices.template.fillOpacity = 0.35
    series.slices.template.propertyFields.fill = "color"
    series.slices.template.propertyFields.stroke = "color"

    series.slices.template.inert = true

    series.hiddenState.properties.startAngle = 90
    series.hiddenState.properties.endAngle = 90

    series.alignLabels = false
    series.labels.template.text = "{value.percent.formatNumber('###.')}%"
    series.labels.template.fill = am4core.color(theme().semiMutedColor)

    series.slices.template.adapter.add("tooltipText", dropSortPrefixAdapter)

    if (this.hasCalculatorResults(metricData.end)) {
      this.addLegend(chart, metricData)
    }

    return chart
  }

  private addLegend(
    chart: am4charts.PieChart,
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    const legend = new am4charts.Legend()
    // Need to set the legend before setting the labels template
    chart.legend = legend

    legend.marginTop = -5
    legend.labels.template.text = ""

    const labelsTemplate = chart.legend.valueLabels.template
    labelsTemplate.fontWeight = "100"
    labelsTemplate.paddingLeft = -1
    labelsTemplate.textAlign = "start"

    legend.itemContainers.template.paddingLeft = 3
    labelsTemplate.adapter.add("html", (value, target) => {
      const dataItem = <am4charts.PieSeriesDataItem | undefined>target.dataItem
      if (dataItem === undefined || !dataItem.hasProperties) return undefined

      const category = dataItem.category
      const itemChange = metricData.changeRateByKey[category] ?? {
        changeRate: 0,
        percentChangeRate: 0,
      }
      return (
        removeDimensionSortPrefix(category) +
        createChangeRateLabel(
          SuperScoreCube.lifeInsuranceIndicatorResult,
          removeDimensionSortPrefix(category),
          {
            // FIXME should this return zero or NA in this scenario?
            value: itemChange.changeRate ?? 0,
            unit: "%",
          }
        )
      )
    })
  }

  private createMiddleLabels(
    chart: am4charts.PieChart,
    metricData: MetricSliced<MemberSnapshotMeasure.countMembers>
  ) {
    const titleLabel = chart.seriesContainer.createChild(am4core.Label)
    titleLabel.hidden = true
    titleLabel.text = "On target"
    titleLabel.horizontalCenter = "middle"
    titleLabel.verticalCenter = "middle"

    const valueLabel = chart.seriesContainer.createChild(am4core.Label)
    valueLabel.hidden = true
    valueLabel.horizontalCenter = "middle"
    valueLabel.verticalCenter = "middle"
    // FIXME do not hard code fonts
    valueLabel.fontFamily = "corpid-caps, gotham narrow"

    this.updateMiddleLabelText(metricData, {titleLabel, valueLabel})

    const series = chart.series.getIndex(0)!

    // Show the label only after the data is displayed in the series
    series.events.once(series.showOnInit ? "appeared" : "ready", () => {
      titleLabel.show()
      valueLabel.show()
    })

    series.events.on("sizechanged", () => {
      if (series.measuredWidth < 200) {
        titleLabel.fontSize = 0
        valueLabel.fontSize = 0
      } else if (series.measuredWidth < 260) {
        const paddingBase = 45
        titleLabel.fontSize = "0.8rem"
        titleLabel.paddingBottom = paddingBase + 50
        valueLabel.fontSize = "1.6rem"
        valueLabel.paddingBottom = paddingBase
      } else if (series.measuredWidth < 320) {
        const paddingBase = 65
        titleLabel.fontSize = "1rem"
        titleLabel.paddingBottom = paddingBase + 60
        valueLabel.fontSize = "2rem"
        valueLabel.paddingBottom = paddingBase
      } else {
        const paddingBase = 85
        titleLabel.fontSize = "1.4rem"
        titleLabel.paddingBottom = paddingBase + 70
        valueLabel.fontSize = "2.5rem"
        valueLabel.paddingBottom = paddingBase
      }
    })

    return {titleLabel, valueLabel}
  }
}
